import { appIntl } from '@nxsys/helpers/Common';
import { PaginationType } from '@nxsys/types/models/Common';
import {
  EmployeeRecordType,
  EmployeeSettingType,
  IHolidayPayEmployee,
  IYTDHistory,
} from '@nxsys/types/models/employee';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { employeeActs } from '../actions';
import {
  GetActivityLogEmployeeListParams,
  GetAssignmentEmployeeListParams,
  GetAttachmentOrderEmployeeListParams,
  GetBIKListingEmployeeListParams,
  GetDeductionEmployeeListParams,
  GetDocumentEmployeeListParams,
  GetEmailLogEmployeeListParams,
  GetEmployeeListParams,
  GetExpenseEmployeeListParams,
  GetExpenseEmployeeListResponse,
  GetExpensePotEmployeeListParams,
  GetExpensePotEmployeeListResponse,
  GetHolidayPayEmployeeListParams,
  GetLeaverHistoryEmployeeListParams,
  GetMessageHistoryEmployeeListParams,
  GetNoteEmployeeListParams,
  GetPaymentEmployeeListParams,
  GetPayslipEmployeeListParams,
  GetReplyMessageHistoryEmployeeListParams,
  GetTimesheetEmployeeListParams,
  GetYTDHistoryEmployeeListParams,
  GetRPNUpdateEmployeeListParams,
} from '../types';
import { ILeaverHistory } from '@nxsys/types/models/employment';
import { DocumentType } from '@nxsys/types/models/document';
import { INote } from '@nxsys/types/models/note';
import { Deduction, Payment } from '../../paymentSetting/types';
import { Assignment } from '../../assignment/types';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { IPayslip } from '@nxsys/types/models/payslip';
import { IAttachmentOrder } from '@nxsys/types/models/attachmentOrder';
import { IExpense } from '@nxsys/types/models/expense';
import { IEmailLog } from '@nxsys/types/models/emailLog';
import { ActivityLog } from '@nxsys/types/models/activityLogs';
import { IMessage, IReplyMessage } from '@nxsys/types/models/message';
import { IBIK } from '@nxsys/types/models/BIK';

export type EmployeeState = {
  employeeDetail: {
    loading: boolean;
    data: EmployeeRecordType | null;
  };
  employeeSetting: {
    loading: boolean;
    data: EmployeeSettingType | null;
  };
  employeeList: {
    loading: boolean;
    list: EmployeeRecordType[];
    pagination: PaginationType;
    queryParams: GetEmployeeListParams | null;
  };
  holidayList: {
    loading: boolean;
    list: IHolidayPayEmployee[];
    pagination: PaginationType;
    queryParams: GetHolidayPayEmployeeListParams | null;
  };
  ytdHistoryList: {
    loading: boolean;
    list: IYTDHistory[];
    pagination: PaginationType;
    queryParams: GetYTDHistoryEmployeeListParams | null;
  };
  leaverHistoryList: {
    loading: boolean;
    list: ILeaverHistory[];
    pagination: PaginationType;
    queryParams: GetLeaverHistoryEmployeeListParams | null;
  };
  documentList: {
    loading: boolean;
    list: DocumentType[];
    pagination: PaginationType;
    queryParams: GetDocumentEmployeeListParams | null;
  };
  noteList: {
    loading: boolean;
    list: INote[];
    pagination: PaginationType;
    queryParams: GetNoteEmployeeListParams | null;
  };
  paymentList: {
    loading: boolean;
    list: Payment[];
    pagination: PaginationType;
    queryParams: GetPaymentEmployeeListParams | null;
  };
  deductionList: {
    loading: boolean;
    list: Deduction[];
    pagination: PaginationType;
    queryParams: GetDeductionEmployeeListParams | null;
  };
  assignmentList: {
    loading: boolean;
    list: Assignment[];
    pagination: PaginationType;
    queryParams: GetAssignmentEmployeeListParams | null;
  };
  timesheetList: {
    loading: boolean;
    list: TimeSheetDataItem[];
    pagination: PaginationType;
    queryParams: GetTimesheetEmployeeListParams | null;
  };
  payslipList: {
    loading: boolean;
    list: IPayslip[];
    pagination: PaginationType;
    queryParams: GetPayslipEmployeeListParams | null;
  };
  attachmentOrderList: {
    loading: boolean;
    list: IAttachmentOrder[];
    pagination: PaginationType;
    queryParams: GetAttachmentOrderEmployeeListParams | null;
  };
  expenseList: {
    loading: boolean;
    list: IExpense[];
    pagination: PaginationType;
    queryParams: GetExpenseEmployeeListParams | null;
    originalData: GetExpenseEmployeeListResponse['data'] | null;
  };
  emailLogList: {
    loading: boolean;
    list: IEmailLog[];
    pagination: PaginationType;
    queryParams: GetEmailLogEmployeeListParams | null;
  };
  activityLogList: {
    loading: boolean;
    list: ActivityLog[];
    pagination: PaginationType;
    queryParams: GetActivityLogEmployeeListParams | null;
  };
  rpnUpdateList: {
    loading: boolean;
    list: any[];
    pagination: PaginationType;
    queryParams: GetRPNUpdateEmployeeListParams | null;
  };
  messageHistoryList: {
    loading: boolean;
    list: IMessage[];
    pagination: PaginationType;
    queryParams: GetMessageHistoryEmployeeListParams | null;
  };
  replyMessageHistoryList: {
    loading: boolean;
    list: IReplyMessage[];
    pagination: PaginationType;
    queryParams: GetReplyMessageHistoryEmployeeListParams | null;
  };
  bikListingList: {
    loading: boolean;
    list: IBIK[];
    pagination: PaginationType;
    queryParams: GetBIKListingEmployeeListParams | null;
  };
  expensePotList: {
    loading: boolean;
    list: GetExpensePotEmployeeListResponse['data'];
    pagination: PaginationType;
    queryParams: GetExpensePotEmployeeListParams | null;
  };
};

const initialState: EmployeeState = {
  employeeDetail: {
    loading: false,
    data: null,
  },
  employeeSetting: {
    loading: false,
    data: null,
  },
  employeeList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  holidayList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  ytdHistoryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  leaverHistoryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  documentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  noteList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  paymentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  deductionList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  assignmentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  timesheetList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  payslipList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  attachmentOrderList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  expenseList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
    originalData: null,
  },
  emailLogList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  activityLogList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  rpnUpdateList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  messageHistoryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  replyMessageHistoryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  bikListingList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  expensePotList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
};

const employeeSlice = createSlice({
  name: 'employee',
  initialState,
  reducers: {
    clearEmployeeDetail: (state) => {
      state.employeeDetail.data = null;
    },
    clearEmployeeSetting: (state) => {
      state.employeeSetting.data = null;
    },
  },
  extraReducers: (builder) => {
    // Get Employee Detail (single record)
    builder.addCase(employeeActs.getEmployeeById.pending, (state) => {
      state.employeeDetail.loading = true;
    });
    builder.addCase(
      employeeActs.getEmployeeById.fulfilled,
      (state, action) => {
        state.employeeDetail.loading = false;
        state.employeeDetail.data = action.payload.data;
      },
    );
    builder.addCase(employeeActs.getEmployeeById.rejected, (state) => {
      state.employeeDetail.loading = false;
    });
    // Get Employee Payroll Setting (single record)
    builder.addCase(
      employeeActs.getPayrollSettingByEmployeeId.pending,
      (state) => {
        state.employeeSetting.loading = true;
      },
    );
    builder.addCase(
      employeeActs.getPayrollSettingByEmployeeId.fulfilled,
      (state, action) => {
        state.employeeSetting.loading = false;
        state.employeeSetting.data = action.payload.data;
      },
    );
    builder.addCase(
      employeeActs.getPayrollSettingByEmployeeId.rejected,
      (state) => {
        state.employeeSetting.loading = false;
      },
    );
    // Get Employee List Records
    builder.addCase(employeeActs.getEmployeeList.pending, (state) => {
      state.employeeList.loading = true;
    });
    builder.addCase(employeeActs.getEmployeeList.fulfilled, (state, action) => {
      state.employeeList.loading = false;
      state.employeeList.list = action.payload.data;
      state.employeeList.pagination = action.payload.pagination;
      state.employeeList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(employeeActs.getEmployeeList.rejected, (state) => {
      state.employeeList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getListError',
        }),
      });
    });
    // Get Holiday Pay Employee List Records
    builder.addCase(employeeActs.getHolidayPayEmployeeList.pending, (state) => {
      state.holidayList.loading = true;
    });
    builder.addCase(
      employeeActs.getHolidayPayEmployeeList.fulfilled,
      (state, action) => {
        state.holidayList.loading = false;
        state.holidayList.list = action.payload.data;
        state.holidayList.pagination = action.payload.pagination;
        state.holidayList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getHolidayPayEmployeeList.rejected,
      (state) => {
        state.holidayList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getHolidayPayEmployeeListError',
          }),
        });
      },
    );

    // Get YTD History Employee List Records
    builder.addCase(employeeActs.getYtdHistoryEmployeeList.pending, (state) => {
      state.ytdHistoryList.loading = true;
    });
    builder.addCase(
      employeeActs.getYtdHistoryEmployeeList.fulfilled,
      (state, action) => {
        state.ytdHistoryList.loading = false;
        state.ytdHistoryList.list = action.payload.data;
        state.ytdHistoryList.pagination = action.payload.pagination;
        state.ytdHistoryList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getYtdHistoryEmployeeList.rejected,
      (state) => {
        state.ytdHistoryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getYtdHistoryEmployeeListError',
          }),
        });
      },
    );

    // Get Document Employee List Records
    builder.addCase(employeeActs.getDocumentEmployeeList.pending, (state) => {
      state.documentList.loading = true;
    });
    builder.addCase(
      employeeActs.getDocumentEmployeeList.fulfilled,
      (state, action) => {
        state.documentList.loading = false;
        state.documentList.list = action.payload.data;
        state.documentList.pagination = action.payload.pagination;
        state.documentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(employeeActs.getDocumentEmployeeList.rejected, (state) => {
      state.documentList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getDocumentEmployeeListError',
        }),
      });
    });

    // Get Leaver History Employee List Records
    builder.addCase(
      employeeActs.getLeaverHistoryEmployeeList.pending,
      (state) => {
        state.leaverHistoryList.loading = true;
      },
    );
    builder.addCase(
      employeeActs.getLeaverHistoryEmployeeList.fulfilled,
      (state, action) => {
        state.leaverHistoryList.loading = false;
        state.leaverHistoryList.list = action.payload.data;
        state.leaverHistoryList.pagination = action.payload.pagination;
        state.leaverHistoryList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getLeaverHistoryEmployeeList.rejected,
      (state) => {
        state.leaverHistoryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getLeaverHistoryEmployeeListError',
          }),
        });
      },
    );

    // Get Note Employee List Records
    builder.addCase(employeeActs.getNoteEmployeeList.pending, (state) => {
      state.noteList.loading = true;
    });
    builder.addCase(
      employeeActs.getNoteEmployeeList.fulfilled,
      (state, action) => {
        state.noteList.loading = false;
        state.noteList.list = action.payload.data;
        state.noteList.pagination = action.payload.pagination;
        state.noteList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(employeeActs.getNoteEmployeeList.rejected, (state) => {
      state.noteList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getNoteEmployeeListError',
        }),
      });
    });

    // Get Payment Employee List Records
    builder.addCase(employeeActs.getPaymentEmployeeList.pending, (state) => {
      state.paymentList.loading = true;
    });
    builder.addCase(
      employeeActs.getPaymentEmployeeList.fulfilled,
      (state, action) => {
        state.paymentList.loading = false;
        state.paymentList.list = action.payload.data;
        state.paymentList.pagination = action.payload.pagination;
        state.paymentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(employeeActs.getPaymentEmployeeList.rejected, (state) => {
      state.paymentList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getPaymentEmployeeListError',
        }),
      });
    });

    // Get Deduction Employee List Records
    builder.addCase(employeeActs.getDeductionEmployeeList.pending, (state) => {
      state.deductionList.loading = true;
    });
    builder.addCase(
      employeeActs.getDeductionEmployeeList.fulfilled,
      (state, action) => {
        state.deductionList.loading = false;
        state.deductionList.list = action.payload.data;
        state.deductionList.pagination = action.payload.pagination;
        state.deductionList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(employeeActs.getDeductionEmployeeList.rejected, (state) => {
      state.deductionList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getDeductionEmployeeListError',
        }),
      });
    });

    // Get Assignment Employee List Records
    builder.addCase(employeeActs.getAssignmentEmployeeList.pending, (state) => {
      state.assignmentList.loading = true;
    });
    builder.addCase(
      employeeActs.getAssignmentEmployeeList.fulfilled,
      (state, action) => {
        state.assignmentList.loading = false;
        state.assignmentList.list = action.payload.data;
        state.assignmentList.pagination = action.payload.pagination;
        state.assignmentList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getAssignmentEmployeeList.rejected,
      (state) => {
        state.assignmentList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getAssignmentEmployeeListError',
          }),
        });
      },
    );

    // Get Timesheet Employee List Records
    builder.addCase(employeeActs.getTimesheetEmployeeList.pending, (state) => {
      state.timesheetList.loading = true;
    });
    builder.addCase(
      employeeActs.getTimesheetEmployeeList.fulfilled,
      (state, action) => {
        state.timesheetList.loading = false;
        state.timesheetList.list = action.payload.data;
        state.timesheetList.pagination = action.payload.pagination;
        state.timesheetList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(employeeActs.getTimesheetEmployeeList.rejected, (state) => {
      state.timesheetList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getTimesheetEmployeeListError',
        }),
      });
    });

    // Get Payslip Employee List Records
    builder.addCase(employeeActs.getPayslipEmployeeList.pending, (state) => {
      state.payslipList.loading = true;
    });
    builder.addCase(
      employeeActs.getPayslipEmployeeList.fulfilled,
      (state, action) => {
        state.payslipList.loading = false;
        state.payslipList.list = action.payload.data;
        state.payslipList.pagination = action.payload.pagination;
        state.payslipList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(employeeActs.getPayslipEmployeeList.rejected, (state) => {
      state.payslipList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getPayslipEmployeeListError',
        }),
      });
    });

    // Get Attachment Order Employee List Records
    builder.addCase(
      employeeActs.getAttachmentOrderEmployeeList.pending,
      (state) => {
        state.attachmentOrderList.loading = true;
      },
    );
    builder.addCase(
      employeeActs.getAttachmentOrderEmployeeList.fulfilled,
      (state, action) => {
        state.attachmentOrderList.loading = false;
        state.attachmentOrderList.list = action.payload.data;
        state.attachmentOrderList.pagination = action.payload.pagination;
        state.attachmentOrderList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getAttachmentOrderEmployeeList.rejected,
      (state) => {
        state.attachmentOrderList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getAttachmentOrderEmployeeListError',
          }),
        });
      },
    );

    // Get Expense Employee List Records
    builder.addCase(employeeActs.getExpenseEmployeeList.pending, (state) => {
      state.expenseList.loading = true;
    });
    builder.addCase(
      employeeActs.getExpenseEmployeeList.fulfilled,
      (state, action) => {
        state.expenseList.loading = false;
        state.expenseList.list = action.payload.data?.expenses;
        state.expenseList.pagination = action.payload.pagination;
        state.expenseList.queryParams = (action.payload as any).queryParams;
        state.expenseList.originalData = action.payload.data;
      },
    );
    builder.addCase(employeeActs.getExpenseEmployeeList.rejected, (state) => {
      state.expenseList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getExpenseEmployeeListError',
        }),
      });
    });

    // Get Email Log Employee List Records
    builder.addCase(employeeActs.getEmailLogEmployeeList.pending, (state) => {
      state.emailLogList.loading = true;
    });
    builder.addCase(
      employeeActs.getEmailLogEmployeeList.fulfilled,
      (state, action) => {
        state.emailLogList.loading = false;
        state.emailLogList.list = action.payload.data;
        state.emailLogList.pagination = action.payload.pagination;
        state.emailLogList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(employeeActs.getEmailLogEmployeeList.rejected, (state) => {
      state.emailLogList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'employee.getEmailLogEmployeeListError',
        }),
      });
    });

    // Get Activity Log Employee List Records
    builder.addCase(
      employeeActs.getActivityLogEmployeeList.pending,
      (state) => {
        state.activityLogList.loading = true;
      },
    );
    builder.addCase(
      employeeActs.getActivityLogEmployeeList.fulfilled,
      (state, action) => {
        state.activityLogList.loading = false;
        state.activityLogList.list = action.payload.data;
        state.activityLogList.pagination = action.payload.pagination;
        state.activityLogList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getActivityLogEmployeeList.rejected,
      (state) => {
        state.activityLogList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getActivityLogEmployeeListError',
          }),
        });
      },
    );

    // Get RPN Update Employee List Records
    builder.addCase(
      employeeActs.getRPNUpdateEmployeeList.pending,
      (state) => {
        state.rpnUpdateList.loading = true;
      },
    );
    builder.addCase(
      employeeActs.getRPNUpdateEmployeeList.fulfilled,
      (state, action) => {
        state.rpnUpdateList.loading = false;
        state.rpnUpdateList.list = action.payload.data;
        state.rpnUpdateList.pagination = action.payload.pagination;
        state.rpnUpdateList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getRPNUpdateEmployeeList.rejected,
      (state) => {
        state.rpnUpdateList.loading = false;
      },
    );

    // Get Message History Employee List Records
    builder.addCase(
      employeeActs.getMessageHistoryEmployeeList.pending,
      (state) => {
        state.messageHistoryList.loading = true;
      },
    );
    builder.addCase(
      employeeActs.getMessageHistoryEmployeeList.fulfilled,
      (state, action) => {
        state.messageHistoryList.loading = false;
        state.messageHistoryList.list = action.payload.data;
        state.messageHistoryList.pagination = action.payload.pagination;
        state.messageHistoryList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getMessageHistoryEmployeeList.rejected,
      (state) => {
        state.messageHistoryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getMessageHistoryEmployeeListError',
          }),
        });
      },
    );

    // Get Reply Message History Employee List Records
    builder.addCase(
      employeeActs.getReplyMessageHistoryEmployeeList.pending,
      (state) => {
        state.replyMessageHistoryList.loading = true;
      },
    );
    builder.addCase(
      employeeActs.getReplyMessageHistoryEmployeeList.fulfilled,
      (state, action) => {
        state.replyMessageHistoryList.loading = false;
        state.replyMessageHistoryList.list = action.payload.data;
        state.replyMessageHistoryList.pagination = action.payload.pagination;
        state.replyMessageHistoryList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getReplyMessageHistoryEmployeeList.rejected,
      (state) => {
        state.replyMessageHistoryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getReplyMessageHistoryEmployeeListError',
          }),
        });
      },
    );

    // Get BIK Listing Employee List Records
    builder.addCase(employeeActs.getBIKListingEmployeeList.pending, (state) => {
      state.bikListingList.loading = true;
    });
    builder.addCase(
      employeeActs.getBIKListingEmployeeList.fulfilled,
      (state, action) => {
        state.bikListingList.loading = false;
        state.bikListingList.list = action.payload.data;
        state.bikListingList.pagination = action.payload.pagination;
        state.bikListingList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getBIKListingEmployeeList.rejected,
      (state) => {
        state.bikListingList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getBIKListingEmployeeListError',
          }),
        });
      },
    );

    // Get Expense Pot Employee List Records
    builder.addCase(employeeActs.getExpensePotEmployeeList.pending, (state) => {
      state.expensePotList.loading = true;
    });
    builder.addCase(
      employeeActs.getExpensePotEmployeeList.fulfilled,
      (state, action) => {
        state.expensePotList.loading = false;
        state.expensePotList.list = action.payload.data;
        state.expensePotList.pagination = action.payload.pagination;
        state.expensePotList.queryParams = (action.payload as any).queryParams;
      },
    );
    builder.addCase(
      employeeActs.getExpensePotEmployeeList.rejected,
      (state) => {
        state.expensePotList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'employee.getExpensePotEmployeeListError',
          }),
        });
      },
    );
  },
});

export const employeeReducer = employeeSlice.reducer;
export const employeeActions = {
  ...employeeSlice.actions,
  ...employeeActs,
};
